/**
 * React Query hooks for the analysis pipeline.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { analysisService } from '@/api/services/analysis.service';
import type { AnalysisStatus } from '@/api/types/analysis.types';

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export const analysisKeys = {
  status: (eventId: string) => ['analysis-status', eventId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Poll analysis status for an event.
 * Refetches every 5 s while the pipeline is running.
 */
export function useAnalysisStatus(eventId: string | undefined) {
  return useQuery<AnalysisStatus>({
    queryKey: analysisKeys.status(eventId ?? ''),
    queryFn: () => analysisService.getAnalysisStatus(eventId!),
    enabled: !!eventId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' ? 5_000 : false;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useStartAnalysis() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      stages,
      forceRerun,
    }: {
      eventId: string;
      stages?: string[];
      forceRerun?: boolean;
    }) => analysisService.startAnalysis(eventId, stages, forceRerun),
    onSuccess: (_data, variables) => {
      toast.success('Analysis pipeline started.');
      qc.invalidateQueries({ queryKey: analysisKeys.status(variables.eventId) });
    },
    onError: () => {
      toast.error('Failed to start analysis pipeline.');
    },
  });
}

export function useRetryFailed() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => analysisService.retryFailed(eventId),
    onSuccess: (_data, eventId) => {
      toast.success('Retrying failed photos...');
      qc.invalidateQueries({ queryKey: analysisKeys.status(eventId) });
    },
    onError: () => {
      toast.error('Failed to retry analysis.');
    },
  });
}

export function useAutoDetectPhases() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => analysisService.autoDetectPhases(eventId),
    onSuccess: (_data, eventId) => {
      toast.success('Phase detection complete.');
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
    onError: () => {
      toast.error('Phase detection failed.');
    },
  });
}
